import React, { useMemo, useState } from "react";
import { AlertTriangle, ArrowDownAZ, BarChart3, Info, ShieldAlert } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { FinanceDataReliability, Task, TaskFinancialState } from "../models/production";
import type { HierarchySnapshot } from "../shared/rollupTypes";
import { cn, formatCurrency } from "../utils";
import {
  buildProjectFinanceVisualDataset,
  type FinanceVisualDataset,
  type FinanceVisualGrouping,
  type FinanceVisualScope,
  type FinanceVisualSort,
  type FinanceVisualStage,
} from "../services/financeVisualAnalysis";

const PERSON_COST_COLOUR = "#b45309";
const GROSS_MARGIN_COLOUR = "#0f766e";

const STAGE_OPTIONS: Array<{ id: FinanceVisualStage; label: string }> = [
  { id: "award", label: "Award" },
  { id: "assigned", label: "Assigned" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
  { id: "live", label: "Live" },
];

const GROUPING_OPTIONS: Array<{ id: FinanceVisualGrouping; label: string }> = [
  { id: "task", label: "By Task" },
  { id: "department", label: "By Department" },
  { id: "episode", label: "By Episode" },
  { id: "asset", label: "By Asset" },
];

const SCOPE_OPTIONS: Array<{ id: FinanceVisualScope; label: string }> = [
  { id: "all", label: "All Tasks" },
  { id: "shots", label: "Shots" },
  { id: "assets", label: "Assets" },
];

const SORT_OPTIONS: Array<{ id: FinanceVisualSort; label: string }> = [
  { id: "highest_bid", label: "Highest bid value" },
  { id: "lowest_margin", label: "Lowest gross margin" },
  { id: "highest_labour", label: "Highest labour cost" },
  { id: "alphabetical", label: "Alphabetical" },
];

function formatPercent(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 10) / 10}%`;
}

function getReliabilityTone(reliability?: FinanceDataReliability) {
  switch (reliability) {
    case "high":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "medium":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "low":
    default:
      return "border-rose-200 bg-rose-50 text-rose-700";
  }
}

function getReliabilityLabel(reliability?: FinanceDataReliability) {
  if (!reliability) return "Unknown confidence";
  return `${reliability} confidence`;
}

function InlineMetric({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className="mt-3 text-2xl font-semibold text-slate-900">{value}</div>
      <div className="mt-2 text-xs leading-5 text-slate-500">{helper}</div>
    </div>
  );
}

function FinanceVisualTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload: FinanceVisualDataset["buckets"][number] }>;
  currency: string;
}) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;

  return (
    <div className="max-w-[280px] rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
      <div className="text-sm font-semibold text-slate-900">{bucket.fullLabel}</div>
      <div className="mt-3 space-y-1.5 text-xs text-slate-600">
        <div>Bid value: {formatCurrency(bucket.bidValue, currency)}</div>
        <div>Person cost: {formatCurrency(bucket.personCost, currency)}</div>
        <div>Gross margin: {formatCurrency(bucket.grossMargin, currency)}</div>
        <div>Gross margin %: {formatPercent(bucket.displayGrossMarginPct)}</div>
        <div>Confidence: {getReliabilityLabel(bucket.reliability)}</div>
        {bucket.missingAttributedCost ? <div>Note: cost has not attributed cleanly into this bucket yet.</div> : null}
      </div>
    </div>
  );
}

export function ProjectFinanceVisualAnalysis({
  snapshot,
  taskEntries,
  currency,
}: {
  snapshot: HierarchySnapshot;
  taskEntries: Array<{ task: Task; financialState?: TaskFinancialState }>;
  currency: string;
}) {
  const [stage, setStage] = useState<FinanceVisualStage>("live");
  const [grouping, setGrouping] = useState<FinanceVisualGrouping>("task");
  const [scope, setScope] = useState<FinanceVisualScope>("all");
  const [sort, setSort] = useState<FinanceVisualSort>("highest_bid");

  const dataset = useMemo(
    () =>
      buildProjectFinanceVisualDataset({
        snapshot,
        taskEntries,
        stage,
        grouping,
        sort,
        scope,
      }),
    [grouping, scope, snapshot, sort, stage, taskEntries]
  );

  const chartHeight = Math.max(320, Math.min(720, dataset.buckets.length * 28 + 140));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Gross Profit Visual Analysis</div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              Vertical stacked bars show how each grouping’s total bid value is split between person cost and remaining gross margin for the selected finance stage.
            </div>
          </div>
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-end">
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {STAGE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setStage(option.id)}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm font-medium transition",
                    stage === option.id ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {GROUPING_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setGrouping(option.id)}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm font-medium transition",
                    grouping === option.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:bg-white"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {SCOPE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setScope(option.id)}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm font-medium transition",
                    scope === option.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:bg-white"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm">
              <ArrowDownAZ className="h-4 w-4" />
              <span>Sort</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as FinanceVisualSort)}
                className="bg-transparent text-sm text-slate-700 outline-none"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <InlineMetric label="Total Bid Value" value={formatCurrency(dataset.totalBidValue, currency)} helper="Total bid value represented in the current grouping and stage view." />
          <InlineMetric label="Total Person Cost" value={formatCurrency(dataset.totalPersonCost, currency)} helper="Current person cost composition for the selected stage." />
          <InlineMetric label="Total Gross Margin" value={formatCurrency(dataset.totalGrossMargin, currency)} helper="What remains after person cost within the same bid total." />
          <InlineMetric label="Gross Margin %" value={formatPercent(dataset.totalGrossMarginPct)} helper={dataset.helperText} />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className={cn("inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium", getReliabilityTone(dataset.reliability))}>
            <ShieldAlert className="h-4 w-4" />
            {getReliabilityLabel(dataset.reliability)}
          </div>
          {dataset.overScheduleBucketCount > 0 ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700">
              <AlertTriangle className="h-4 w-4" />
              {dataset.overScheduleBucketCount} grouping{dataset.overScheduleBucketCount === 1 ? "" : "s"} include over-schedule work
            </div>
          ) : null}
          {dataset.lowConfidenceBucketCount > 0 ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700">
              <ShieldAlert className="h-4 w-4" />
              {dataset.lowConfidenceBucketCount} grouping{dataset.lowConfidenceBucketCount === 1 ? "" : "s"} include low-confidence tasks
            </div>
          ) : null}
        </div>

        {!dataset.buckets.length ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
            <div className="text-sm font-medium text-slate-900">No finance chart data is available yet</div>
            <div className="mt-2 text-sm text-slate-500">
              This usually means the project does not yet have enough bid, task, or finance-stage data to produce a grouped cost-versus-margin view.
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-600">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Task Cost vs Gross Margin</div>
                    <div className="text-xs text-slate-500">
                      One bar per {grouping === "task" ? "task" : grouping}. Use the scope toggle to move between shot and asset work.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5">
                  <Info className="h-4 w-4 text-slate-400" />
                  <span className="max-w-[240px] text-[10px] font-medium leading-tight text-slate-500">
                    {dataset.helperText}
                  </span>
                </div>
              </div>
              <div style={{ height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dataset.buckets} margin={{ top: 20, right: 24, left: 12, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#94a3b8", fontSize: 12, fontWeight: 500 }}
                      interval={0}
                      angle={-18}
                      textAnchor="end"
                      height={70}
                      dy={8}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#94a3b8", fontSize: 12 }}
                      tickFormatter={(value) => formatCurrency(Number(value || 0), currency)}
                    />
                    <Tooltip content={<FinanceVisualTooltip currency={currency} />} cursor={{ fill: "rgba(148, 163, 184, 0.08)" }} />
                    <Bar dataKey="personCost" stackId="finance" radius={[3, 3, 3, 3]} name="Person Cost" barSize={grouping === "task" ? 28 : 40}>
                      {dataset.buckets.map((entry) => (
                        <Cell key={`person-${entry.id}`} fill={PERSON_COST_COLOUR} />
                      ))}
                    </Bar>
                    <Bar dataKey="grossMargin" stackId="finance" radius={[3, 3, 3, 3]} name="Gross Margin" barSize={grouping === "task" ? 28 : 40}>
                      {dataset.buckets.map((entry) => (
                        <Cell key={`margin-${entry.id}`} fill={GROSS_MARGIN_COLOUR} />
                      ))}
                    </Bar>
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="circle"
                      wrapperStyle={{ paddingBottom: "18px", fontSize: "10px", fontWeight: "bold", textTransform: "uppercase" }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="grid grid-cols-[minmax(0,1.8fr)_repeat(4,minmax(0,1fr))_minmax(0,0.9fr)] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                <div>{GROUPING_OPTIONS.find((option) => option.id === grouping)?.label || "Grouping"}</div>
                <div>Bid Value</div>
                <div>Person Cost</div>
                <div>Gross Margin</div>
                <div>Margin %</div>
                <div>Warnings</div>
              </div>
              {dataset.buckets.map((bucket) => (
                <div key={bucket.id} className="grid grid-cols-[minmax(0,1.8fr)_repeat(4,minmax(0,1fr))_minmax(0,0.9fr)] gap-3 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900">{bucket.fullLabel}</div>
                    <div className="mt-1 truncate text-xs text-slate-500">
                      {bucket.taskCount} task{bucket.taskCount === 1 ? "" : "s"} • {getReliabilityLabel(bucket.reliability)}
                    </div>
                  </div>
                  <div className="text-slate-700">{formatCurrency(bucket.bidValue, currency)}</div>
                  <div className="text-slate-700">{formatCurrency(bucket.personCost, currency)}</div>
                  <div className="font-medium text-slate-900">{formatCurrency(bucket.grossMargin, currency)}</div>
                  <div className="text-slate-700">
                    {bucket.missingAttributedCost ? (
                      <span className="text-amber-700">Pending cost attribution</span>
                    ) : (
                      formatPercent(bucket.displayGrossMarginPct)
                    )}
                  </div>
                  <div>
                    {bucket.warningLabel ? (
                      <div className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {bucket.warningLabel}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
